import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CheckCircle, Sparkle, Buildings, BookOpen, CircleNotch, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import LicenseAgreementText from './LicenseAgreementText';
import { PLAN_BASELINES, LIMIT_LABELS } from '@/lib/licensePlans';

const STEPS = ['org', 'terms', 'reveal', 'preseedIntro', 'preseedProgress'];

// Mirrors backend/seed.py's YUHASPRO_BRANCHES[0] -- shown as a preview of
// what the preseed step will create when no branch exists yet. If a branch
// already exists (e.g. re-entering the wizard after a partial run), the real
// branch's own data is shown/edited instead -- see OrgStep.
const DEFAULT_BRANCH_PREVIEW = {
  name: 'Thane',
  address: 'B-12, Hiranandani Estate, Ghodbunder Road, Thane 400607',
  phone: '022-25891000',
  capacity: 400,
};

function StepDots({ step }) {
  const idx = STEPS.indexOf(step);
  return (
    <div className="flex items-center gap-1.5 justify-center mb-8" data-testid="onboarding-step-dots">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center">
          <div
            className={`w-2 h-2 rounded-full transition-all duration-300 ${i <= idx ? 'w-6' : ''}`}
            style={{ backgroundColor: i <= idx ? '#E04B2A' : 'var(--border, #e5e5e5)' }}
          />
          {i < STEPS.length - 1 && <div className="w-3 h-px bg-border mx-0.5" />}
        </div>
      ))}
    </div>
  );
}

function Shell({ step, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10" data-testid="onboarding-wizard">
      <div className="w-full max-w-xl">
        <StepDots step={step} />
        <div className="border border-border rounded-2xl bg-card p-7 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}

// Animates from `from` to `to`, holding at `from` for `holdMs` before it
// starts moving -- used to let the baseline number visibly "sit" before the
// bonus count-up kicks in.
function CountUpFrom({ from, to, duration = 1200, holdMs = 0 }) {
  const [val, setVal] = useState(from);
  useEffect(() => {
    let raf;
    let start;
    setVal(from);
    const timer = setTimeout(() => {
      const step = (ts) => {
        if (!start) start = ts;
        const progress = Math.min(1, (ts - start) / duration);
        // ease-out-back-ish overshoot for a little punch on landing
        const eased = 1 - Math.pow(1 - progress, 3);
        setVal(Math.round(from + (to - from) * eased));
        if (progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, holdMs);
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, duration, holdMs]);
  return <>{val.toLocaleString('en-IN')}</>;
}

// Game-style XP/progress bar: fills from a starting % up to 100%, with a
// glowing gradient fill and a shine sweep -- matches the app's brand accents
// (#E04B2A orange) rather than a generic loading-bar look.
function GameBar({ fromPct, toPct, active, color = '#E04B2A' }) {
  // Mounts at 0% then transitions to fromPct almost immediately, so the bar
  // fills in parallel with the 0->baseline count-up instead of just
  // appearing already at width on first paint.
  const [filled, setFilled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFilled(true), 50);
    return () => clearTimeout(t);
  }, []);
  const width = active ? toPct : (filled ? fromPct : 0);
  return (
    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden relative border border-border/50">
      <div
        className="h-full rounded-full relative overflow-hidden"
        style={{
          width: `${width}%`,
          background: `linear-gradient(90deg, ${color}, #F97316)`,
          transition: `width ${active ? '2.2s' : '1.3s'} cubic-bezier(0.22, 1, 0.36, 1)`,
          boxShadow: active ? `0 0 8px ${color}99` : 'none',
        }}
      >
        {active && (
          <span
            className="absolute inset-y-0 w-8 opacity-60"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent)', animation: 'ob-shine 2.2s ease-out' }}
          />
        )}
      </div>
    </div>
  );
}

// Confetti burst -- CSS-only, three staggered waves so it fills the ~4s
// baseline hold and continues a little past it into the bonus reveal,
// instead of a single quick pop.
function ConfettiBurst({ fire }) {
  const colors = ['#E04B2A', '#F97316', '#10A882', '#2B6EF0', '#E5A100', '#8B5CF6'];
  const particles = Array.from({ length: 60 }, (_, i) => {
    const angle = (i / 60) * Math.PI * 2 + Math.random() * 0.3;
    const dist = 90 + Math.random() * 130;
    const wave = i % 3 * 900; // three waves, ~0.9s apart
    return {
      id: i,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist - 50,
      color: colors[i % colors.length],
      rot: Math.round(Math.random() * 900 - 450),
      delay: wave + Math.random() * 400,
      dur: 2.2 + Math.random() * 0.8,
      size: 5 + Math.round(Math.random() * 5),
    };
  });
  if (!fire) return null;
  return (
    <div className="pointer-events-none absolute inset-x-0 top-8 h-0 flex justify-center z-10" data-testid="ob-confetti">
      {particles.map(p => (
        <span
          key={p.id}
          style={{
            position: 'absolute',
            width: p.size, height: p.size * 1.6,
            backgroundColor: p.color,
            borderRadius: 1,
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--rot': `${p.rot}deg`,
            animation: `ob-confetti ${p.dur}s ${p.delay}ms cubic-bezier(0.2,0.7,0.3,1) forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes ob-confetti {
          0% { transform: translate(0,0) rotate(0deg); opacity: 1; }
          65% { opacity: 1; }
          100% { transform: translate(var(--dx), calc(var(--dy) + 220px)) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ── Step 1: Verify Organization Details ──
function OrgStep({ onNext }) {
  const [orgName, setOrgName] = useState('');
  const [branch, setBranch] = useState(null);
  const [editingBranch, setEditingBranch] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/settings/organization').catch(() => ({ data: {} })),
      api.get('/api/branches').catch(() => ({ data: { branches: [] } })),
    ]).then(([orgRes, branchRes]) => {
      setOrgName(orgRes.data.name || 'YuHasPro');
      const existing = (branchRes.data.branches || [])[0];
      setBranch(existing
        ? { name: existing.name, address: existing.address || '', phone: existing.phone || '', capacity: existing.capacity || 0 }
        : { ...DEFAULT_BRANCH_PREVIEW });
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/onboarding/confirm-details');
      onNext(branch);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save organization details');
    } finally { setSaving(false); }
  };

  if (!branch) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  return (
    <form onSubmit={handleSubmit} data-testid="onboarding-org-step" className="space-y-4">
      <div className="text-center mb-2">
        <h2 className="font-display text-xl font-bold text-foreground">Welcome to Admitrix</h2>
        <p className="text-xs text-muted-foreground mt-1">First, let&rsquo;s confirm your organization&rsquo;s details</p>
      </div>

      <div>
        <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Organization Name</Label>
        <Input data-testid="ob-org-name" value={orgName} disabled className="h-9 rounded-lg text-sm disabled:opacity-100 disabled:cursor-default bg-secondary/50" />
      </div>

      <div className="border border-border rounded-xl p-3.5 bg-secondary/30">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branch — {branch.name}</span>
          {!editingBranch && (
            <button type="button" data-testid="ob-branch-edit" onClick={() => setEditingBranch(true)} className="flex items-center gap-1 text-[10px] font-bold" style={{ color: '#E04B2A' }}>
              <PencilSimple size={11} weight="bold" /> Edit
            </button>
          )}
        </div>
        {editingBranch ? (
          <div className="space-y-2.5">
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Address</Label>
              <Input data-testid="ob-branch-address" value={branch.address} onChange={e => setBranch({ ...branch, address: e.target.value })} className="h-8 rounded-lg text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Phone</Label>
                <Input data-testid="ob-branch-phone" value={branch.phone} onChange={e => setBranch({ ...branch, phone: e.target.value })} className="h-8 rounded-lg text-xs" />
              </div>
              <div>
                <Label className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Capacity</Label>
                <Input type="number" data-testid="ob-branch-capacity" value={branch.capacity} onChange={e => setBranch({ ...branch, capacity: Number(e.target.value) })} className="h-8 rounded-lg text-xs" />
              </div>
            </div>
            <button type="button" data-testid="ob-branch-done" onClick={() => setEditingBranch(false)} className="text-[10px] font-bold" style={{ color: '#E04B2A' }}>Done</button>
          </div>
        ) : (
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            <p>{branch.address}</p>
            <p>{branch.phone}</p>
            <p>Capacity: {branch.capacity}</p>
          </div>
        )}
      </div>

      <Button type="submit" data-testid="ob-org-continue" disabled={saving} className="w-full h-9 rounded-lg text-xs font-bold text-white disabled:opacity-60" style={{ backgroundColor: '#E04B2A' }}>
        {saving ? 'Saving...' : 'Confirm & Continue'}
      </Button>
    </form>
  );
}

// ── Step 2: Accept Software License Agreement ──
function TermsStep({ onNext }) {
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await api.post('/api/onboarding/accept-terms');
      onNext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record acceptance');
    } finally { setAccepting(false); }
  };

  return (
    <div data-testid="onboarding-terms-step">
      <div className="text-center mb-4">
        <h2 className="font-display text-xl font-bold text-foreground">Software License Agreement</h2>
        <p className="text-xs text-muted-foreground mt-1">Please review and accept to continue</p>
      </div>
      <div className="max-h-72 overflow-y-auto border border-border rounded-xl p-4 bg-secondary/30" data-testid="ob-terms-scrollbox">
        <LicenseAgreementText />
      </div>
      <div className="flex items-start gap-2 mt-4">
        <Checkbox id="ob-agree" data-testid="ob-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} />
        <Label htmlFor="ob-agree" className="text-xs font-normal text-foreground leading-snug">
          I have read and agree to the Software License Agreement on behalf of my organization.
        </Label>
      </div>
      <Button
        data-testid="ob-terms-accept"
        onClick={handleAccept}
        disabled={!agreed || accepting}
        className="w-full h-9 rounded-lg text-xs font-bold text-white mt-4 disabled:opacity-50"
        style={{ backgroundColor: '#E04B2A' }}
      >
        {accepting ? 'Recording...' : 'Accept & Continue'}
      </Button>
    </div>
  );
}

// ── Step 3: Animated Plan Reveal ──
const REVEAL_ORDER = ['leads_this_year', 'enrolled_students', 'courses', 'batches'];
const REVEAL_ICONS = { leads_this_year: Sparkle, enrolled_students: Sparkle, courses: BookOpen, batches: BookOpen };

function RevealStep({ onNext }) {
  const [license, setLicense] = useState(null);
  // 'baseline' (holding at Standard's published numbers) -> 'bonus'
  // (numbers + bars animate up to the real bonus totals). Confetti runs on
  // its own separate timer -- it fires right at the start and its burst
  // duration fills the baseline hold, continuing past it into the bonus
  // reveal, rather than only starting once the hold ends.
  const [phase, setPhase] = useState('baseline');
  const [confettiOn, setConfettiOn] = useState(false);

  useEffect(() => {
    api.get('/api/settings/license').then(r => setLicense(r.data)).catch(() => setLicense({}));
  }, []);

  useEffect(() => {
    if (!license) return;
    // Confetti starts almost immediately, just before the hold begins, and
    // is long enough to span the full pause and spill into the reveal.
    const confettiTimer = setTimeout(() => setConfettiOn(true), 150);
    // Long enough pause for the user to actually register the baseline
    // numbers before anything moves -- this used to be 1s and read as an
    // instant jump straight to the bonus total.
    const bonusTimer = setTimeout(() => setPhase('bonus'), 4000);
    return () => { clearTimeout(confettiTimer); clearTimeout(bonusTimer); };
  }, [license]);

  if (!license) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  const baselines = PLAN_BASELINES[license.plan_name] || {};
  const planLabel = license.plan_name ? `${license.plan_name.charAt(0).toUpperCase()}${license.plan_name.slice(1)}` : 'Standard';
  const isBonus = phase === 'bonus';

  return (
    <div data-testid="onboarding-reveal-step" className="text-center relative" data-phase={phase}>
      <ConfettiBurst fire={confettiOn} />
      <div className="text-4xl mb-2 animate-bounce" style={{ animationDuration: '1.4s', animationIterationCount: 2 }}>🎉</div>
      <h2 className="font-display text-2xl font-extrabold text-foreground">You&rsquo;ve been enrolled in the {planLabel} Plan!</h2>
      <p className="text-xs text-muted-foreground mt-1.5 mb-6 transition-opacity duration-300">
        {isBonus ? <span style={{ color: '#10A882' }} className="font-bold">Plus bonus limits added for this year! 🎁</span> : 'Here’s what’s included in your plan'}
      </p>

      <div className="grid grid-cols-2 gap-3 text-left">
        {REVEAL_ORDER.map((key) => {
          const entry = license.limits?.[key] || {};
          const baseline = baselines[key] ?? entry.max ?? 0;
          const max = entry.max ?? 0;
          const bonus = max > baseline ? max - baseline : 0;
          const pctBaseline = max > 0 ? Math.round((baseline / max) * 100) : 100;
          const Icon = REVEAL_ICONS[key];
          return (
            <div key={key} className="border border-border rounded-xl p-3.5 bg-secondary/30" data-testid={`ob-reveal-${key}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={13} style={{ color: '#E04B2A' }} />
                <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground">{LIMIT_LABELS[key]}</span>
              </div>
              <div className="font-display text-2xl font-extrabold text-foreground tabular-nums">
                <CountUpFrom from={isBonus ? baseline : 0} to={isBonus ? max : baseline} duration={isBonus ? 2200 : 1300} holdMs={0} />
              </div>
              <div className="mt-2 mb-1.5">
                <GameBar fromPct={pctBaseline} toPct={100} active={isBonus} />
              </div>
              <p
                className="text-[10px] font-bold"
                style={{
                  color: '#10A882',
                  visibility: bonus > 0 ? 'visible' : 'hidden',
                  opacity: isBonus ? 1 : 0,
                  transform: isBonus ? 'scale(1)' : 'scale(0.6)',
                  transition: 'opacity 0.5s ease-out 0.15s, transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s',
                }}
              >
                +{bonus.toLocaleString('en-IN')} bonus this year
              </p>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground mt-5 transition-opacity duration-500" style={{ opacity: isBonus ? 1 : 0 }}>
        Plus, we&rsquo;re setting up a starter branch and 50-course catalog for you &mdash; also a bonus, not part of the Standard plan.
      </p>

      <Button data-testid="ob-reveal-continue" onClick={onNext} className="w-full h-9 rounded-lg text-xs font-bold text-white mt-5" style={{ backgroundColor: '#E04B2A' }}>
        Continue
      </Button>
      <style>{`@keyframes ob-shine { from { transform: translateX(-40px); } to { transform: translateX(160px); } }`}</style>
    </div>
  );
}

// ── Step 4: Preseed intro ──
function PreseedIntroStep({ onNext }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/api/courses'), api.get('/api/branches')]).then(([c, b]) => {
      setCounts({
        courses: (c.data.courses || c.data || []).length,
        branches: (b.data.branches || b.data || []).length,
      });
    }).catch(() => setCounts({ courses: 0, branches: 0 }));
  }, []);

  if (!counts) return <div className="text-sm text-muted-foreground text-center py-12 font-mono">loading...</div>;

  const hasData = counts.courses > 0 || counts.branches > 0;
  const courseWord = hasData ? counts.courses : 50;
  const branchWord = hasData ? counts.branches : 1;

  return (
    <div data-testid="onboarding-preseed-intro-step" className="text-center">
      <div className="text-4xl mb-2">🚀</div>
      <h2 className="font-display text-xl font-bold text-foreground">One last step!</h2>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
        You&rsquo;ve opted for preseed data with <strong className="text-foreground">{courseWord} courses</strong> and{' '}
        <strong className="text-foreground">{branchWord} branch{branchWord === 1 ? '' : 'es'}</strong>.
        Press OK to start the process.
      </p>
      <Button data-testid="ob-preseed-start" onClick={onNext} className="w-full h-9 rounded-lg text-xs font-bold text-white mt-6" style={{ backgroundColor: '#E04B2A' }}>
        OK, Start Setup
      </Button>
    </div>
  );
}

// ── Step 5: Preseed progress (animated) ──
function PreseedProgressStep({ branch, onFinish }) {
  const [branchDone, setBranchDone] = useState(false);
  const [coursesDone, setCoursesDone] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const apiCall = api.post('/api/onboarding/preseed', { branch });
        // Minimum staged timings so the animation reads as real work, even
        // when the API itself responds near-instantly (idempotent no-op for
        // a client that already has real data).
        await new Promise(r => setTimeout(r, 900));
        if (!cancelled) setBranchDone(true);
        await new Promise(r => setTimeout(r, 900));
        if (!cancelled) setCoursesDone(true);
        await apiCall;
        await new Promise(r => setTimeout(r, 400));
        if (!cancelled) setAllDone(true);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Setup failed — you can retry from Settings later.');
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="onboarding-preseed-progress-step" className="text-center">
      <h2 className="font-display text-xl font-bold text-foreground mb-6">Setting things up for you...</h2>
      <div className="space-y-3 text-left max-w-xs mx-auto">
        <ChecklistRow icon={Buildings} label="Creating your branch" done={branchDone} />
        <ChecklistRow icon={BookOpen} label="Loading your course catalog" done={coursesDone} />
      </div>
      {error && <p className="text-xs text-destructive mt-4">{error}</p>}
      {allDone && !error && (
        <div className="mt-6" style={{ animation: 'ob-fade-in 0.4s ease-out forwards' }}>
          <div className="text-3xl mb-2">✅</div>
          <p className="text-sm font-bold text-foreground mb-4">All set! Your CRM is ready to go.</p>
          <Button data-testid="ob-finish" onClick={onFinish} className="w-full h-9 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
            Enter Admitrix
          </Button>
        </div>
      )}
      <style>{`@keyframes ob-fade-in { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
}

function ChecklistRow({ icon: Icon, label, done }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-secondary/30">
      {done ? (
        <CheckCircle size={18} weight="fill" style={{ color: '#10A882' }} />
      ) : (
        <CircleNotch size={18} className="animate-spin text-muted-foreground" />
      )}
      <span className={`text-sm ${done ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
        <Icon size={13} className="inline mr-1.5 -mt-0.5" />{label}
      </span>
    </div>
  );
}

export default function OnboardingWizard({ onComplete, initialStep = 'org' }) {
  const [step, setStep] = useState(initialStep);
  const [branch, setBranch] = useState(null);

  return (
    <Shell step={step}>
      {step === 'org' && <OrgStep onNext={(b) => { setBranch(b); setStep('terms'); }} />}
      {step === 'terms' && <TermsStep onNext={() => setStep('reveal')} />}
      {step === 'reveal' && <RevealStep onNext={() => setStep('preseedIntro')} />}
      {step === 'preseedIntro' && <PreseedIntroStep onNext={() => setStep('preseedProgress')} />}
      {step === 'preseedProgress' && <PreseedProgressStep branch={branch} onFinish={onComplete} />}
    </Shell>
  );
}
